import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, User as UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VersionConfig, AssessmentState, AssessmentResults } from '@/types/assessment';
import { mockResults } from '@/data/mockResults';
import { useAuth } from '@/contexts/AuthContext';
import { useBasePath } from '@/contexts/PortalContext';
import { saveResults, generateResultsProfile, generateResultsFull, resolveSelectionsToLabels } from '@/lib/results';
import { track, identifyUser } from '@/lib/analytics';
import { applyTheme, resetTheme } from '@/lib/themes';
import JourneyPath from './JourneyPath';
import BoardGamePath from './BoardGamePath';
import BoardGamePathMini from './BoardGamePathMini';
import ScreenRenderer from './ScreenRenderer';
import RiasecProfile from './RiasecProfile';
import { scoreRiasec, topRiasec } from '@/lib/riasec';
import ResultsView from './ResultsView';
import Confetti from './Confetti';
import TerrainBackground from './TerrainBackground';

interface AssessmentFlowProps {
  config: VersionConfig;
}

// Page transition variants
// Simple slide transitions (lightweight, no jank on mobile)
const pageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
  }),
};

const pageTransition = {
  type: 'tween' as const,
  duration: 0.25,
  ease: 'easeInOut' as const,
};

const AssessmentFlow = ({ config }: AssessmentFlowProps) => {
  const { user, profile } = useAuth();
  const basePath = useBasePath();
  const prefersReducedMotion = useReducedMotion();

  const reducedVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };
  const activePageVariants = prefersReducedMotion ? reducedVariants : pageVariants;
  const activePageTransition = prefersReducedMotion ? { duration: 0.2 } : pageTransition;
  const storageKey = `truepath-progress-${config.edition}`;

  const [state, setState] = useState<AssessmentState>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.currentScreen >= 0 && !parsed.completed) return parsed;
      }
    } catch { /* ignore corrupt data */ }
    return { currentScreen: -1, selections: {}, openTexts: {}, completed: false };
  });
  const [showConfetti, setShowConfetti] = useState(false);

  // Save progress on every state change (except completed results)
  useEffect(() => {
    if (state.currentScreen >= 0 && !state.completed) {
      localStorage.setItem(storageKey, JSON.stringify(state));
    }
  }, [state, storageKey]);
  const [generating, setGenerating] = useState(false);
  const [generatingStage, setGeneratingStage] = useState(0);
  const [shareId, setShareId] = useState<string | null>(null);
  const [direction, setDirection] = useState(1);
  const [currentResults, setCurrentResults] = useState<AssessmentResults>(mockResults);

  const totalScreens = config.screens.length;
  const isIntro = state.currentScreen === -1;
  const isLastScreen = state.currentScreen === totalScreens - 1;

  const toggleSelection = useCallback((screenId: string, optionId: string) => {
    setState((prev) => {
      const current = prev.selections[screenId] || [];
      const screen = config.screens.find((s) => s.id === screenId);
      const max = screen?.maxSelections;

      if (current.includes(optionId)) {
        return { ...prev, selections: { ...prev.selections, [screenId]: current.filter((id) => id !== optionId) } };
      }

      if (max && current.length >= max) return prev;

      return { ...prev, selections: { ...prev.selections, [screenId]: [...current, optionId] } };
    });
  }, [config.screens]);

  const setOpenText = useCallback((screenId: string, text: string) => {
    setState((prev) => ({ ...prev, openTexts: { ...prev.openTexts, [screenId]: text } }));
  }, []);

  const goNext = () => {
    setDirection(1);
    // Track assessment start (intro → first screen)
    if (isIntro) {
      track({ event: 'assessment_started', props: { edition: config.edition } });
      if (user) identifyUser(user.id, { email: user.email });
    }
    if (isLastScreen) {
      setGenerating(true);
      setGeneratingStage(0);

      const run = async () => {
        const selectedTheme = Object.keys(state.selections)
          .flatMap(k => state.selections[k])
          .find(s => s.startsWith('theme-'));
        if (selectedTheme) applyTheme(selectedTheme);

        // Stage 1: resolve labels
        const labeledSelections = resolveSelectionsToLabels(state.selections, config.screens);

        setGeneratingStage(1);

        // Two-phase generation: show profile fast, then load full details in background
        let results = { ...mockResults, _fallback: true };
        try {
          // Phase 1: Profile only (~15-20s) — show results immediately
          const profileResults = await generateResultsProfile(config.edition, labeledSelections, state.openTexts, profile?.school_id);
          results = profileResults;

          setGeneratingStage(2);
          await new Promise(r => setTimeout(r, 400));

          // Show phase 1 results immediately
          const id = await saveResults(config.edition, state, results, user?.id);
          setShareId(id);
          track({
            event: 'assessment_completed',
            props: { edition: config.edition, total_steps: totalScreens, used_fallback: false, phase: 'profile' },
          });
          setShowConfetti(true);
          setGenerating(false);
          setGeneratingStage(0);
          localStorage.removeItem(storageKey);
          setState((prev) => ({ ...prev, completed: true }));
          setCurrentResults(results);

          // Phase 2: Full details in background — merge when ready
          generateResultsFull(config.edition, labeledSelections, state.openTexts, profile?.school_id)
            .then((fullResults) => {
              setCurrentResults(fullResults);
              // Update saved results with full data
              saveResults(config.edition, state, fullResults, user?.id);
            })
            .catch((err) => {
              console.error('Phase 2 (full details) failed, keeping profile results:', err);
            });

          return; // Skip the fallback path below
        } catch (err) {
          console.error('AI generation failed:', err);
          results = { ...mockResults, _fallback: true };
        }

        setGeneratingStage(2);
        await new Promise(r => setTimeout(r, 600));

        const id = await saveResults(config.edition, state, results, user?.id);
        setShareId(id);
        track({
          event: 'assessment_completed',
          props: { edition: config.edition, total_steps: totalScreens, used_fallback: !!results._fallback },
        });
        setShowConfetti(true);
        setGenerating(false);
        setGeneratingStage(0);
        localStorage.removeItem(storageKey);
        setState((prev) => ({ ...prev, completed: true }));
        setCurrentResults(results);
      };

      run();
      return;
    }
    setState((prev) => ({ ...prev, currentScreen: prev.currentScreen + 1 }));
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    setTimeout(() => document.getElementById('main-content')?.focus(), 300);
  };

  const goBack = () => {
    setDirection(-1);
    if (state.completed) {
      setState((prev) => ({ ...prev, completed: false, currentScreen: totalScreens - 1 }));
      window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
      return;
    }
    setState((prev) => ({ ...prev, currentScreen: Math.max(-1, prev.currentScreen - 1) }));
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const goToScreen = (index: number) => {
    if (index < state.currentScreen) {
      setDirection(-1);
      setState((prev) => ({ ...prev, currentScreen: index }));
      window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    }
  };

  const startOver = () => {
    resetTheme();
    localStorage.removeItem(storageKey);
    setState({ currentScreen: -1, selections: {}, openTexts: {}, completed: false });
    setShowConfetti(false);
    setShareId(null);
    setCurrentResults(mockResults);
  };

  const generatingStages = [
    { label: 'Learning about you...' },
    { label: 'Finding your best-fit careers...' },
    { label: 'Your True Path is ready!' },
  ];
  const currentStageInfo = generatingStages[generatingStage] || generatingStages[0];

  const affirmations = [
    { text: "Your path is unique. That is your superpower.", author: "TruePath" },
    { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
    { text: "You don't have to see the whole staircase. Just take the first step.", author: "Martin Luther King Jr." },
    { text: "What makes you different is what makes you powerful.", author: "TruePath" },
    { text: "Your career isn't a straight line. It's a portfolio of experiences.", author: "TruePath" },
    { text: "Every expert was once a beginner.", author: "Helen Hayes" },
    { text: "Your skills are the seeds. The right environment makes them grow.", author: "TruePath" },
    { text: "Don't ask what the world needs. Ask what makes you come alive.", author: "Howard Thurman" },
    { text: "The best way to predict the future is to create it.", author: "Peter Drucker" },
    { text: "You are not behind. You are exactly where you need to be.", author: "TruePath" },
    { text: "Your potential isn't fixed. It grows with every challenge you take on.", author: "TruePath" },
    { text: "It always seems impossible until it's done.", author: "Nelson Mandela" },
    { text: "Education is the most powerful weapon which you can use to change the world.", author: "Nelson Mandela" },
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "You have brains in your head. You have feet in your shoes. You can steer yourself any direction you choose.", author: "Dr. Seuss" },
  ];

  const [currentAffirmation, setCurrentAffirmation] = useState(0);

  useEffect(() => {
    if (!generating) return;
    const startIndex = Math.floor(Math.random() * affirmations.length);
    setCurrentAffirmation(startIndex);
    const interval = setInterval(() => {
      setCurrentAffirmation(prev => (prev + 1) % affirmations.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [generating]); // eslint-disable-line react-hooks/exhaustive-deps

  if (generating) {
    const affirmation = affirmations[currentAffirmation];
    return (
      <div
        className="min-h-screen bg-journey-atmosphere flex items-center justify-center relative overflow-hidden"
        role="status"
        aria-live="polite"
        aria-label={currentStageInfo.label}
      >
        <TerrainBackground count={4} />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 w-full max-w-4xl mx-auto px-6 py-12 flex flex-col items-center justify-center gap-10"
        >
          {/* Stage label */}
          <AnimatePresence mode="wait">
            <motion.h2
              key={`label-${generatingStage}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="text-2xl md:text-3xl font-display text-foreground text-center"
            >
              {currentStageInfo.label}
            </motion.h2>
          </AnimatePresence>

          {/* Rotating affirmation — full width, centerpiece */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentAffirmation}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.7 }}
              className="glass-card py-12 px-8 md:py-16 md:px-16 text-center space-y-6 w-full"
            >
              <p className="text-2xl md:text-4xl lg:text-5xl italic text-foreground/90 leading-[1.3] font-display">
                "{affirmation.text}"
              </p>
              <p className="text-base md:text-lg text-primary font-semibold">
                — {affirmation.author}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Progress bar - continuous indeterminate loop */}
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Generating your path"
            className="w-full max-w-md h-1.5 bg-muted rounded-full overflow-hidden relative"
          >
            <motion.div
              className="absolute inset-y-0 w-1/3 rounded-full"
              style={{ background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent)))' }}
              animate={{ x: ['-100%', '300%'] }}
              transition={{ duration: 1.8, ease: 'easeInOut' as const, repeat: Infinity }}
            />
          </div>

          <p className="text-xs text-muted-foreground/60">
            Grounded in real career data from the U.S. Department of Labor
          </p>
        </motion.div>
      </div>
    );
  }

  if (state.completed) {
    return (
      <>
        <Confetti active={showConfetti} />
        <ResultsView
          results={currentResults}
          config={config}
          onStartOver={startOver}
          shareId={shareId}
          selections={state.selections}
        />
      </>
    );
  }

  const currentScreenConfig = isIntro ? null : config.screens[state.currentScreen];

  return (
    <div className="min-h-screen bg-journey-atmosphere flex flex-col relative overflow-hidden">
      <TerrainBackground count={4} />
      <div className="noise-overlay" />

      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="px-6 py-4 flex items-center justify-between border-b border-border/30 sticky top-0 z-20 bg-background/80 backdrop-blur-md"
      >
        <div className="flex items-center gap-3">
          <span className="text-primary font-display text-lg font-bold">TruePath</span>
          <span className="text-muted-foreground text-xs px-2 py-0.5 rounded-full bg-secondary border border-border">
            {config.label}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {!isIntro && (
            <div className="hidden sm:block lg:hidden w-48">
              <JourneyPath total={totalScreens} current={state.currentScreen} screenLabels={config.screens.map((s: { title: string }) => s.title)} />
            </div>
          )}
          {user ? (
            <Link to={`${basePath}/dashboard`} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors">
              <UserIcon className="w-3 h-3" aria-hidden="true" /> {user.email?.split('@')[0]}
            </Link>
          ) : (
            <Link to={`${basePath}/auth`} state={{ from: window.location.pathname }} className="text-xs text-primary hover:underline flex items-center gap-1">
              <UserIcon className="w-3 h-3" aria-hidden="true" /> Sign in
            </Link>
          )}
        </div>
      </motion.header>

      {/* Mobile board game path */}
      {!isIntro && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="sm:hidden px-4 pt-3 relative z-10"
        >
          <BoardGamePathMini
            total={totalScreens}
            current={state.currentScreen}
            screenLabels={config.screens.map((s: { title: string }) => s.title)}
            onNavigateToStep={goToScreen}
          />
        </motion.div>
      )}

      {/* Content — split-panel: sidebar trail (desktop) + question area */}
      <main id="main-content" tabIndex={-1} className="flex-1 flex relative z-10 outline-none">
        {/* Desktop sidebar trail */}
        {!isIntro && !state.completed && (
          <aside className="hidden lg:block w-72 flex-shrink-0 sticky top-20 self-start p-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <BoardGamePath
              total={totalScreens}
              current={state.currentScreen}
              screenLabels={config.screens.map((s: { title: string }) => s.title)}
              onNavigateToStep={goToScreen}
            />
          </aside>
        )}

        {/* Question area */}
        <div className="flex-1 flex items-center justify-center px-4 py-8" style={{ perspective: '1200px' }}>
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={direction}>
            {isIntro ? (
              <motion.div
                key="intro"
                custom={direction}
                variants={activePageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={activePageTransition}
                className="text-center space-y-8"
              >
                <div className="space-y-4">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-20 h-20 mx-auto rounded-2xl bg-primary/20 flex items-center justify-center glow-teal"
                  >
                    <Sparkles className="w-10 h-10 text-primary" aria-hidden="true" />
                  </motion.div>
                  <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                    className="text-4xl md:text-5xl font-display font-bold text-foreground leading-tight"
                  >
                    Your Career<br />
                    <span className="text-gradient-teal">True Path</span>
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.4 }}
                    className="text-lg text-muted-foreground max-w-md mx-auto"
                  >
                    {config.tagline}
                  </motion.p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                  className="glass-card p-6 text-left space-y-3 max-w-md mx-auto"
                >
                  <p className="text-sm text-muted-foreground">This takes about <span className="text-foreground font-medium">5-7 minutes</span>. You'll get:</p>
                  <ul className="space-y-2 text-sm">
                    {[
                      'A personalized career profile',
                      '3 unique career paths tailored to you',
                      'Actionable roadmap with timelines',
                      'Skills to stack for the AI economy',
                    ].map((item, i) => (
                      <motion.li
                        key={item}
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 + i * 0.08, duration: 0.3 }}
                        className="flex items-center gap-2"
                      >
                        <span className="text-primary">✦</span> {item}
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.3 }}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 40px -5px hsl(12 60% 45% / 0.35)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={goNext}
                  className="px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold text-lg glow-teal transition-all hover:brightness-110"
                >
                  Let's go →
                </motion.button>
              </motion.div>
            ) : currentScreenConfig ? (
              <motion.div
                key={currentScreenConfig.id}
                custom={direction}
                variants={activePageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={activePageTransition}
                className="space-y-6"
              >
                {/* Question header card */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05, duration: 0.4 }}
                  className="glass-card-elevated p-6 md:p-8 space-y-3"
                >
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-primary">{state.currentScreen + 1}</span>
                    </div>
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      Step {state.currentScreen + 1} of {totalScreens}
                    </span>
                  </div>

                  <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground tracking-tight leading-tight">
                    {currentScreenConfig.title}
                  </h2>

                  <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                    {currentScreenConfig.subtitle}
                  </p>

                  {currentScreenConfig.encouragement && (
                    <p className="text-sm text-primary/80 font-medium italic border-l-2 border-primary/20 pl-3">
                      {currentScreenConfig.encouragement}
                    </p>
                  )}

                  {currentScreenConfig.maxSelections && (
                    <MaxReachedIndicator
                      current={(state.selections[currentScreenConfig.id] || []).length}
                      max={currentScreenConfig.maxSelections}
                    />
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <ScreenRenderer
                    screen={currentScreenConfig}
                    selections={state.selections[currentScreenConfig.id] || []}
                    openText={state.openTexts[currentScreenConfig.id] || ''}
                    onToggle={(id) => toggleSelection(currentScreenConfig.id, id)}
                    onTextChange={(text) => setOpenText(currentScreenConfig.id, text)}
                    socialPlatforms={config.socialPlatforms}
                    edition={config.edition}
                  />
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
        </div>
      </main>

      {/* Live RIASEC indicator — shows the user how their interest profile
          is taking shape as they answer. Suppressed until they have at least
          one non-zero RIASEC score so the empty state doesn't feel clinical. */}
      {!isIntro && !state.completed && (() => {
        const liveScores = scoreRiasec(state.selections);
        const hasSignal = topRiasec(liveScores, 1).length > 0;
        if (!hasSignal) return null;
        return (
          <div className="px-6 pb-3 relative z-10">
            <RiasecProfile scores={liveScores} variant="compact" />
          </div>
        );
      })()}

      {/* Footer nav */}
      {!isIntro && (
        <motion.footer
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="px-6 py-4 flex items-center justify-between border-t border-border/30 relative z-10"
        >
          <motion.button
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={goBack}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
          </motion.button>
          {isLastScreen ? (
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={goNext}
              className="relative group flex items-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-br from-primary via-primary to-accent text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-shadow overflow-hidden"
            >
              {/* Soft pulsing glow ring (anticipation) */}
              <motion.span
                aria-hidden="true"
                className="absolute inset-0 rounded-xl"
                style={{ background: 'radial-gradient(circle at 50% 50%, hsl(var(--primary) / 0.45), transparent 70%)' }}
                animate={{ opacity: [0.4, 0.75, 0.4], scale: [1, 1.06, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* Sliding shimmer on hover */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/25 to-transparent"
              />
              <span className="relative">Discover Your Path</span>
              <motion.span
                className="relative"
                animate={{ rotate: [0, 12, -8, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Sparkles className="w-4 h-4" aria-hidden="true" />
              </motion.span>
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05, x: 3 }}
              whileTap={{ scale: 0.95 }}
              onClick={goNext}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:brightness-110 transition-all"
            >
              Next <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </motion.button>
          )}
        </motion.footer>
      )}
    </div>
  );
};

const MaxReachedIndicator = ({ current, max }: { current: number; max: number }) => {
  const atMax = current >= max;
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className={`text-xs font-medium ${atMax ? 'text-accent' : 'text-muted-foreground'}`}
    >
      {current}/{max} selected {atMax && '— Max reached'}
    </div>
  );
};

export default AssessmentFlow;
